/**
 * c-booking-tracking.js
 * 店舗見学予約（Appointo）の「予約完了」を検知して CV を計測する。
 *
 * ■ なぜこの仕組みが要るか
 * Appointo の「予約確認後にリダイレクトされるURL」は、リダイレクト先にパラメータを
 * 一切付けない（実測で search / hash とも空）。URL への到達だけを根拠に発火させると、
 * サンクスページを直接開いた人・リロードした人まで CV に数えてしまう。
 *
 * 予約確定時に app.appointo.me/scripttag/book_slot への通信が発生することを実測済みなので、
 * これをフラグ源にする。
 *
 *   [予約ページ / 商品ページ]
 *     fetch / XMLHttpRequest をラップして book_slot の成功を検知
 *       └ sessionStorage にフラグを保存
 *            ↓ Appointo がサンクスページへリダイレクト
 *   [サンクスページ]
 *     フラグがある時だけ GA4 / アドエビス / Meta / X を発火 → 発火後フラグを削除
 *
 * ■ 二重計上を防ぐ仕掛け
 * - 直リンク・リロード・ブックマークからの流入ではフラグが無いので発火しない
 * - 発火後すぐフラグを消すので、リロードしても再発火しない
 * - sessionStorage なのでタブを閉じれば消える
 *
 * ■ 注意
 * 予約は無料のため金額は 0。CV 件数のみを計上する（ROAS には影響しない）。
 */
(function () {
  'use strict';

  var FLAG_KEY = 'cene:booking_completed';
  var BOOK_SLOT_PATH = '/scripttag/book_slot';
  // 完了ページは店舗ごとに /pages/reservation-complete-<店舗>（例 -harajuku）。
  // 末尾の店舗名を計測に載せて、店舗ごとの予約数を分けて見られるようにする（2026-10-16）。
  // 店舗名なしの /pages/reservation-complete も拾う（その時は店舗 = 未設定）。
  var THANKS_PATH_RE = /\/pages\/reservation-complete(?:-([a-z0-9-]+))?\/?$/;

  var ADEBIS_ARGUMENT = 'ZqpoaqX2'; // 共通タグと同一（layout/theme.liquid）
  // 購入（complete）と分けるための計測ポイント。店舗があれば reservation-<店舗>（例 reservation-harajuku）。
  // アドエビス管理画面のコンバージョン設定のページIDと一字一句そろえること（ずれると CV が数えられない）
  var ADEBIS_PAGE_ID_BASE = 'reservation';
  var GA4_EVENT = 'reservation_complete';
  var META_EVENT = 'ReservationComplete';

  // X（旧 Twitter）広告。ピクセルは Shopify の X 連携アプリが読み込むが、Shopify の
  // サンドボックス（web pixel）の中で動くためテーマからは呼べない。予約完了の時だけ
  // ここで基本コードを読み込み、イベントを送る（2026-10-20・岩田さんの依頼）。
  // イベントは X 広告マネージャ › イベントマネージャ「予約完了（原宿店）」（Custom）。
  var X_PIXEL_ID = 'rh4h4';
  var X_EVENT_ID = 'tw-rh4h4-rhmi0';

  // イベント（期間限定の予約ページ）の店舗名。ここに載っている店舗名の予約は、
  // 店舗の来店予約と別の CV として数えられるよう、GA4 / Meta / X に別のイベントで送る
  // （2026-10-28・CENE HOLIDAY EVENT COLLECTION。岩田さん「原宿予約と分けて計測」）。
  // 載っていない店舗名（harajuku 等）は上の来店予約のイベントのまま。
  // アドエビスは page_id（reservation-<店舗名>）で分かれるので一覧に関係しない。
  // 次のイベントでも同じイベント名を使い回す（媒体の CV 設定を作り直さなくて済む）。店舗名で見分ける。
  var EVENT_STORES = ['holiday-2026'];
  var EVENT_GA4_EVENT = 'event_reservation_complete';
  var EVENT_META_EVENT = 'EventReservationComplete';
  // X 広告マネージャ › イベントマネージャ「予約完了（イベント）」（Custom）
  var EVENT_X_EVENT_ID = 'tw-rh4h4-ri3j0';

  var DEBUG = /[?&]booking_debug=1/.test(location.search);

  // gtag / fbq の読み込みを待つ上限。Appointo のリダイレクト直後は
  // これらがまだ未定義のことがあるため（実測）。
  var TAG_WAIT_LIMIT_MS = 8000;
  var TAG_POLL_INTERVAL_MS = 100;

  function log() {
    if (!DEBUG) return;
    try {
      console.log.apply(console, ['[booking]'].concat([].slice.call(arguments)));
    } catch (e) {}
  }

  /* ------------------------------------------------------------------
     1. 予約確定の検知
     ------------------------------------------------------------------ */

  /**
   * 予約完了フラグを保存する。
   * Appointo のレスポンス形式は保証されないため、取れた情報だけを拾う。
   */
  function saveFlag(payload) {
    var data = { at: Date.now() };

    if (payload && typeof payload === 'object') {
      // book_slot のレスポンスは { status, booking: { id, timestring, ... }, customer: { booking_id, ... } }
      // （2026-10-02 実測）。booking.id が Appointo の予約番号で、管理画面の予約と1対1に対応する。
      var booking = payload.booking || {};
      var customer = payload.customer || {};
      var id = booking.id || customer.booking_id || null;
      data.bookingId = id === null ? null : String(id);
      // 予約日時（店舗のタイムゾーン付き。例: 2027-01-25T11:00:00+09:00）
      data.slotDatetime = booking.timestring || booking.selected_time || null;
    }

    // Appointo のレスポンスからは予約日時が取れないことがある（実測）。
    // サンクスページで予約サマリーを出すため、画面に出ている選択日時を控えておく。
    // 例: 「11:00, 金, 8月 28, 2026」
    if (!data.slotDatetime) {
      var selected = document.querySelector('.appointo-time-selected');
      if (selected) data.slotText = selected.textContent.trim();
    }

    try {
      sessionStorage.setItem(FLAG_KEY, JSON.stringify(data));
      log('flag saved', data);
    } catch (e) {
      // プライベートブラウジング等で sessionStorage が使えない場合は計測を諦める
      log('sessionStorage unavailable', e);
    }
  }

  function isBookSlotUrl(url) {
    return typeof url === 'string' && url.indexOf(BOOK_SLOT_PATH) !== -1;
  }

  /** レスポンス本文を JSON として読み、フラグを保存する（読めなくてもフラグは立てる） */
  function handleResponseBody(promiseOfText) {
    promiseOfText
      .then(function (text) {
        var json = null;
        try {
          json = JSON.parse(text);
        } catch (e) {}
        saveFlag(json);
      })
      .catch(function () {
        saveFlag(null);
      });
  }

  // fetch をラップ
  if (typeof window.fetch === 'function') {
    var originalFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = originalFetch.apply(this, arguments);

      if (isBookSlotUrl(url)) {
        promise
          .then(function (response) {
            if (!response || !response.ok) return;
            handleResponseBody(response.clone().text());
          })
          .catch(function () {});
      }
      return promise;
    };
  }

  // XMLHttpRequest もラップする（Appointo が将来 fetch 以外を使っても取りこぼさないため）
  if (window.XMLHttpRequest && XMLHttpRequest.prototype.open) {
    var originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (isBookSlotUrl(url)) {
        this.addEventListener('load', function () {
          if (this.status < 200 || this.status >= 300) return;
          var json = null;
          try {
            json = JSON.parse(this.responseText);
          } catch (e) {}
          saveFlag(json);
        });
      }
      return originalOpen.apply(this, arguments);
    };
  }

  /* ------------------------------------------------------------------
     2. サンクスページでの発火
     ------------------------------------------------------------------ */

  /**
   * 条件が満たされるまで待ってからコールバックを呼ぶ。
   * 満たされないままタイムアウトした場合は ok=false で呼ぶ。
   */
  function waitFor(isReady, callback) {
    var deadline = Date.now() + TAG_WAIT_LIMIT_MS;
    (function poll() {
      if (isReady()) {
        callback(true);
        return;
      }
      if (Date.now() > deadline) {
        callback(false);
        return;
      }
      setTimeout(poll, TAG_POLL_INTERVAL_MS);
    })();
  }

  function readAndClearFlag() {
    var raw = null;
    try {
      raw = sessionStorage.getItem(FLAG_KEY);
      // 読めた時点で消す。以降のリロードでは発火しない
      sessionStorage.removeItem(FLAG_KEY);
    } catch (e) {
      return null;
    }
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return {};
    }
  }

  function isEventStore(store) {
    for (var i = 0; i < EVENT_STORES.length; i++) {
      if (EVENT_STORES[i] === store) return true;
    }
    return false;
  }

  function sendGa4(data) {
    if (typeof window.gtag !== 'function') {
      log('gtag unavailable');
      return;
    }
    window.gtag('event', isEventStore(data.store) ? EVENT_GA4_EVENT : GA4_EVENT, {
      store: data.store || '(not set)',
      booking_id: data.bookingId || '(not set)',
      slot_datetime: data.slotDatetime || '(not set)',
      value: 0,
      currency: 'JPY'
    });
    log('GA4 sent');
  }

  /**
   * アドエビス。共通タグ（layout/theme.liquid）とは別に、page_id 付きで push してから
   * cmt.js を読み込む。書式はチェックアウトの custom pixel（document/ga4/adebis-custom-pixel.js）
   * と揃えてあり、page_id だけを分けている。
   */
  function sendAdebis(data) {
    window.ebis = window.ebis || [];
    window.ebis.push({
      argument: ADEBIS_ARGUMENT,
      page_id: data.store ? ADEBIS_PAGE_ID_BASE + '-' + data.store : ADEBIS_PAGE_ID_BASE,
      amount: 0, // 予約は無料
      other1: data.bookingId || '',
      other2: data.slotDatetime || ''
    });

    (function (a, d, e, b, i, s) {
      window[i] = window[i] || [];
      var f = function (a, d, e, b, i, s) {
        var o = a.getElementsByTagName(d)[0],
          h = a.createElement(d),
          t = 'text/javascript';
        h.type = t;
        h.async = e;
        h.onload = function () {
          window[i].init({ argument: s, auto: true });
        };
        h._p = o;
        return h;
      };
      var h = f(a, d, e, b, i, s),
        l = '//taj',
        j = b + s + '/cmt.js';
      h.src = l + '1.' + j;
      h._p.parentNode.insertBefore(h, h._p);
      h.onerror = function (k) {
        k = f(a, d, e, b, i, s);
        k.src = l + '2.' + j;
        k._p.parentNode.insertBefore(k, k._p);
      };
    })(document, 'script', true, 'ebis.ne.jp/', 'ebis', ADEBIS_ARGUMENT);
    log('adebis sent');
  }

  /**
   * Meta。URL ベースのカスタムコンバージョンだとサンクスページに到達しただけで
   * 発火してしまうため、こちらでカスタムイベントを送る。
   * Meta 管理画面ではこのイベント名でカスタムコンバージョンを作る。
   */
  function sendMeta(data) {
    if (typeof window.fbq !== 'function') {
      log('fbq unavailable');
      return;
    }
    window.fbq('trackCustom', isEventStore(data.store) ? EVENT_META_EVENT : META_EVENT, {
      store: data.store || '(not set)',
      booking_id: data.bookingId || '(not set)',
      slot_datetime: data.slotDatetime || '(not set)',
      value: 0,
      currency: 'JPY'
    });
    log('Meta sent');
  }

  /**
   * X。基本コード（uwt.js）を読み込んで予約完了イベントを送る。
   * conversion_id に予約番号を入れ、同じ予約が二重に数えられないようにする。
   * 基本コードの config でこのページの閲覧も1回送られる（X 連携アプリ側の閲覧と重なるのはこのページだけ）。
   */
  function privacyApi() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    return cp && typeof cp.marketingAllowed === 'function' ? cp : null;
  }

  /** Shopify の同意 API（プライバシーバナー）。同意が確かめられない時は送らない */
  function marketingAllowed() {
    try {
      var cp = privacyApi();
      return cp ? cp.marketingAllowed() === true : false;
    } catch (e) {
      return false;
    }
  }

  function sendX(data) {
    if (!marketingAllowed()) {
      log('X skipped (no marketing consent)');
      return 'skipped (no consent)';
    }
    /* eslint-disable */
    !function (e, t, n, s, u, a) {
      e.twq || (s = e.twq = function () {
        s.exe ? s.exe.apply(s, arguments) : s.queue.push(arguments);
      }, s.version = '1.1', s.queue = [], u = t.createElement(n), u.async = !0,
      u.src = 'https://static.ads-twitter.com/uwt.js', a = t.getElementsByTagName(n)[0],
      a.parentNode.insertBefore(u, a));
    }(window, document, 'script');
    /* eslint-enable */
    window.twq('config', X_PIXEL_ID);
    var params = {};
    if (data.bookingId) params.conversion_id = 'booking-' + data.bookingId;
    window.twq('event', isEventStore(data.store) ? EVENT_X_EVENT_ID : X_EVENT_ID, params);
    log('X sent');
    return 'sent';
  }

  function fireOnThanksPage() {
    var match = location.pathname.match(THANKS_PATH_RE);
    if (!match) return;

    var data = readAndClearFlag();
    if (!data) {
      // 予約を経ずにサンクスページを開いた場合。CV 水増しを防ぐためここで止める
      log('no flag - skip (direct access or reload)');
      return;
    }
    data.store = match[1] || '';

    log('firing', data);

    // 検証用。?booking_debug=1 のときだけ各媒体の送信結果を残す
    var result = { data: data, ga4: 'pending', adebis: null, meta: 'pending', x: null };
    if (DEBUG) window.__ceneBookingDebug = result;

    // アドエビスは自前で cmt.js を読み込むので、他タグの初期化を待つ必要がない
    try {
      sendAdebis(data);
      result.adebis = 'sent';
    } catch (e) {
      result.adebis = 'error: ' + e;
      log('adebis error', e);
    }

    // X も自前で基本コードを読み込む（twq はキューを持つので読み込み完了を待たなくてよい）。
    // 広告の同意を確かめてから送るので、Shopify の同意 API が使えるようになるまで待つ
    waitFor(
      function () {
        return !!privacyApi();
      },
      function (ok) {
        if (!ok) {
          result.x = 'skipped (privacy api unavailable)';
          log('X skipped: privacy api unavailable');
          return;
        }
        try {
          result.x = sendX(data);
        } catch (e) {
          result.x = 'error: ' + e;
          log('X error', e);
        }
      }
    );

    // GA4 と Meta は、Appointo のリダイレクト直後だと gtag / fbq がまだ読み込まれて
    // いないことがある（実測で取りこぼしを確認）。使えるようになるまで待ってから送る。
    waitFor(
      function () {
        return typeof window.gtag === 'function';
      },
      function (ok) {
        if (!ok) {
          result.ga4 = 'gtag unavailable (timeout)';
          log('GA4 timeout');
          return;
        }
        try {
          sendGa4(data);
          result.ga4 = 'sent';
        } catch (e) {
          result.ga4 = 'error: ' + e;
          log('GA4 error', e);
        }
      }
    );

    waitFor(
      function () {
        return typeof window.fbq === 'function';
      },
      function (ok) {
        if (!ok) {
          result.meta = 'fbq unavailable (timeout)';
          log('Meta timeout');
          return;
        }
        try {
          sendMeta(data);
          result.meta = 'sent';
        } catch (e) {
          result.meta = 'error: ' + e;
          log('Meta error', e);
        }
      }
    );

    // 予約内容をページ側で表示したい場合に使えるよう、発火した内容を通知する
    try {
      document.dispatchEvent(new CustomEvent('cene:booking_completed', { detail: data }));
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fireOnThanksPage);
  } else {
    fireOnThanksPage();
  }
})();
